import React, { useEffect, useMemo, useState } from "react";
import { SeoAPI } from "../../api/seo";
import {
  SeoActionResponse,
  SeoActivity,
  SeoDashboard,
  SeoDashboardRow,
  SeoDashboardSitemap,
  SeoUrlStatus,
} from "../../types/seo";
import { useViewportMatch } from "../../hooks/useViewportMatch";

type SeoFormState = {
  siteUrl: string;
  searchConsolePropertyUrl: string;
  sitemapPath: string;
  serviceAccountJson: string;
  autoSubmitSitemapOnProductChange: boolean;
  autoInspectUpdatedUrls: boolean;
  autoSubmitSitemapOnContentChange: boolean;
  autoSyncLocalSitemapSnapshot: boolean;
  fullSyncInspectLimit: number;
};

const surfaceStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(148,163,184,.18)",
  borderRadius: 24,
  boxShadow: "0 18px 44px rgba(15,23,42,.08)",
  minWidth: 0,
};

const cardStyle: React.CSSProperties = {
  ...surfaceStyle,
  padding: 20,
};

const smallCardStyle: React.CSSProperties = {
  ...surfaceStyle,
  padding: "16px 18px",
};

const labelStyle: React.CSSProperties = {
  display: "grid",
  gap: 8,
  fontSize: 13,
  color: "#334155",
  fontWeight: 700,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid rgba(148,163,184,.34)",
  background: "#fff",
  borderRadius: 14,
  padding: "12px 14px",
  fontSize: 14,
  color: "#0f172a",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 144,
  resize: "vertical",
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace",
};

const buttonBase: React.CSSProperties = {
  border: "none",
  borderRadius: 999,
  padding: "11px 16px",
  fontWeight: 800,
  fontSize: 14,
  cursor: "pointer",
  transition: "transform .16s ease, box-shadow .16s ease, opacity .16s ease",
};

const tableCellStyle: React.CSSProperties = {
  padding: "12px 10px",
  borderBottom: "1px solid rgba(226,232,240,.9)",
  fontSize: 14,
  color: "#1e293b",
  verticalAlign: "top",
};

const subtleTextStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.55,
};

function defaultFormState(): SeoFormState {
  return {
    siteUrl: "https://beyondpendants.com/",
    searchConsolePropertyUrl: "sc-domain:beyondpendants.com",
    sitemapPath: "/sitemap.xml",
    serviceAccountJson: "",
    autoSubmitSitemapOnProductChange: true,
    autoInspectUpdatedUrls: true,
    autoSubmitSitemapOnContentChange: true,
    autoSyncLocalSitemapSnapshot: false,
    fullSyncInspectLimit: 10,
  };
}

function settingsToForm(settings: SeoDashboard["settings"]): SeoFormState {
  return {
    siteUrl: settings.siteUrl || "https://beyondpendants.com/",
    searchConsolePropertyUrl:
      settings.searchConsolePropertyUrl || "sc-domain:beyondpendants.com",
    sitemapPath: settings.sitemapPath || "/sitemap.xml",
    serviceAccountJson: "",
    autoSubmitSitemapOnProductChange: Boolean(
      settings.autoSubmitSitemapOnProductChange,
    ),
    autoInspectUpdatedUrls: Boolean(settings.autoInspectUpdatedUrls),
    autoSubmitSitemapOnContentChange: Boolean(
      settings.autoSubmitSitemapOnContentChange,
    ),
    autoSyncLocalSitemapSnapshot: Boolean(
      settings.autoSyncLocalSitemapSnapshot,
    ),
    fullSyncInspectLimit: Number(settings.fullSyncInspectLimit || 10),
  };
}

function formatInteger(value: number) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Number.isFinite(value) ? value : 0,
  );
}

function formatPercent(value: number) {
  return `${(Number.isFinite(value) ? value * 100 : 0).toFixed(2)}%`;
}

function formatPosition(value: number) {
  return Number.isFinite(value) ? value.toFixed(1) : "0.0";
}

function formatDateTime(value?: string) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function responsiveGrid(minWidth: number) {
  return `repeat(auto-fit, minmax(min(100%, ${minWidth}px), 1fr))`;
}

function joinSiteUrl(siteUrl: string, pathname: string) {
  const base = String(siteUrl || "").trim();
  const nextPath = String(pathname || "").trim();
  if (!base) return nextPath;
  try {
    const parsed = new URL(base);
    const basePath = parsed.pathname === "/" ? "" : parsed.pathname.replace(/\/+$/, "");
    const normalizedPath =
      nextPath === "/"
        ? "/"
        : `/${nextPath.replace(/^\/+/, "").replace(/\/{2,}/g, "/")}`;
    parsed.pathname = `${basePath}${normalizedPath}`.replace(/\/{2,}/g, "/") || "/";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return `${base.replace(/\/+$/, "")}/${nextPath.replace(/^\/+/, "")}`;
  }
}

type InspectLoopProgress = {
  active: boolean;
  total: number;
  completed: number;
  successCount: number;
  indexedCount: number;
  notIndexedCount: number;
  errorCount: number;
  skippedCount: number;
  currentUrl: string;
  lastMessage: string;
  startedAt: string;
  finishedAt?: string;
};

type UrlStatusFilter = "all" | "indexed" | "not_indexed" | "errors" | "uninspected";

type InlineFeedback = {
  tone: "neutral" | "success" | "warning" | "error";
  title: string;
  detail: string;
  items?: string[];
};

function feedbackStyle(tone: InlineFeedback["tone"]): React.CSSProperties {
  if (tone === "success") {
    return {
      background: "rgba(16,185,129,.10)",
      border: "1px solid rgba(16,185,129,.18)",
      color: "#047857",
    };
  }
  if (tone === "warning") {
    return {
      background: "rgba(245,158,11,.10)",
      border: "1px solid rgba(245,158,11,.18)",
      color: "#92400e",
    };
  }
  if (tone === "error") {
    return {
      background: "rgba(239,68,68,.08)",
      border: "1px solid rgba(239,68,68,.16)",
      color: "#b91c1c",
    };
  }
  return {
    background: "rgba(15,23,42,.05)",
    border: "1px solid rgba(148,163,184,.18)",
    color: "#334155",
  };
}

function containsText(value: string, pattern: string) {
  return String(value || "").toLowerCase().includes(String(pattern || "").toLowerCase());
}

function inspectionResponseIsIndexed(response: SeoActionResponse | null | undefined) {
  const result = response?.result as
    | {
        inspectionResult?: {
          indexStatusResult?: {
            verdict?: string;
            coverageState?: string;
            indexingState?: string;
          };
        };
      }
    | undefined;
  const index = result?.inspectionResult?.indexStatusResult;
  const verdict = String(index?.verdict || "").trim();
  const coverageState = String(index?.coverageState || "").trim();
  const indexingState = String(index?.indexingState || "").trim();

  return (
    containsText(verdict, "pass") ||
    containsText(coverageState, "submitted and indexed") ||
    containsText(coverageState, "indexed") ||
    containsText(indexingState, "indexed")
  );
}

function buildUrlStatusCounts(rows: SeoUrlStatus[]) {
  return {
    all: rows.length,
    indexed: rows.filter((row) => row.isIndexed).length,
    not_indexed: rows.filter(
      (row) => row.lastInspectionStatus === "success" && !row.isIndexed,
    ).length,
    errors: rows.filter((row) => row.lastInspectionStatus === "error").length,
    uninspected: rows.filter((row) => row.lastInspectionStatus === "never").length,
  };
}

function filterUrlStatuses(rows: SeoUrlStatus[], filter: UrlStatusFilter) {
  if (filter === "indexed") {
    return rows.filter((row) => row.isIndexed);
  }
  if (filter === "not_indexed") {
    return rows.filter(
      (row) => row.lastInspectionStatus === "success" && !row.isIndexed,
    );
  }
  if (filter === "errors") {
    return rows.filter((row) => row.lastInspectionStatus === "error");
  }
  if (filter === "uninspected") {
    return rows.filter((row) => row.lastInspectionStatus === "never");
  }
  return rows;
}

function getServiceAccountJsonFeedback(
  value: string,
  hasStoredCredentials: boolean,
): InlineFeedback {
  const raw = String(value || "").trim();
  if (!raw) {
    return hasStoredCredentials
      ? {
          tone: "warning",
          title: "Stored credentials already exist",
          detail:
            "Paste a new JSON file only if you want to replace the encrypted service-account key already saved on the backend.",
          items: [
            "The private key is intentionally not reloaded into the browser after refresh.",
          ],
        }
      : {
          tone: "neutral",
          title: "Paste a Google service-account JSON file",
          detail:
            "The dashboard will validate the JSON, save it encrypted, then test Search Console access against beyondpendants.com.",
        };
  }

  let parsed: Record<string, unknown>;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
      return {
        tone: "error",
        title: "JSON is not valid for a service account",
        detail: "The uploaded value must be a JSON object from Google Cloud.",
      };
    }
    parsed = candidate as Record<string, unknown>;
  } catch {
    return {
      tone: "error",
      title: "JSON parsing failed",
      detail: "Fix the JSON syntax before saving.",
    };
  }

  const clientEmail = String(parsed.client_email || "").trim();
  const privateKey = String(parsed.private_key || "").replace(/\\n/g, "\n").trim();

  if (!clientEmail || !privateKey) {
    return {
      tone: "error",
      title: "Required fields are missing",
      detail: "The JSON must include both client_email and private_key.",
    };
  }

  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clientEmail)) {
    return {
      tone: "error",
      title: "client_email is not valid",
      detail: "Use the exact service-account email generated by Google Cloud.",
    };
  }

  const hasPemMarkers =
    privateKey.includes("-----BEGIN PRIVATE KEY-----") &&
    privateKey.includes("-----END PRIVATE KEY-----");
  if (!hasPemMarkers) {
    return {
      tone: "error",
      title: "private_key is not a real PEM key",
      detail:
        "This looks like placeholder text, not the actual private key from the Google service-account JSON file.",
      items: [`Service account detected: ${clientEmail}`],
    };
  }

  return {
    tone: "success",
    title: "JSON structure looks valid",
    detail:
      "Save SEO Settings to encrypt this key on the backend, then the dashboard will test whether it can access the configured Search Console property.",
    items: [`Service account: ${clientEmail}`],
  };
}

function getConnectionSuggestions(
  connection: SeoDashboard["searchConsole"]["connection"] | undefined,
  fallbackPropertyUrl: string,
  fallbackServiceAccountEmail: string,
): string[] {
  const state = String(connection?.state || "").trim();
  const message = String(connection?.message || "").trim();
  const lowered = message.toLowerCase();
  const propertyUrl = String(
    connection?.propertyUrl || fallbackPropertyUrl || "",
  ).trim();
  const serviceAccountEmail = String(
    connection?.credentialEmail || fallbackServiceAccountEmail || "",
  ).trim();
  const expectedCredentialEmail = String(
    connection?.expectedCredentialEmail || "",
  ).trim();
  const suggestions: string[] = [];

  if (state === "missing_credentials" || !serviceAccountEmail) {
    suggestions.push("Paste and save a Google service-account JSON file first.");
    return suggestions;
  }

  if (state === "wrong_credential") {
    suggestions.push(
      `Upload the correct service-account JSON so the credential email is ${expectedCredentialEmail || "the expected Search Console service account"}.`,
    );
    return suggestions;
  }

  if (state === "wrong_property") {
    suggestions.push(
      `Set the Search Console property in this dashboard to exactly ${propertyUrl || "the property returned by Google"}.`,
    );
    suggestions.push(
      `Make sure ${serviceAccountEmail} is added to that exact property as an Owner or Full user.`,
    );
    return suggestions;
  }

  if (state === "no_accessible_properties") {
    suggestions.push(
      `The credential authenticated, but Google returned no Search Console properties. Re-check that ${serviceAccountEmail} was added to sc-domain:beyondpendants.com and wait a few minutes for propagation.`,
    );
    return suggestions;
  }

  if (state === "google_error" || lowered.includes("invalid input")) {
    suggestions.push(
      "Review the Google API error and the accessible-properties list below. If the property is missing there, the issue is Search Console access rather than the URL itself.",
    );
  }

  if (!suggestions.length) {
    suggestions.push(
      "Run Test Connection after saving and make sure the property URL in this dashboard exactly matches the property in Google Search Console.",
    );
  }

  return suggestions;
}

function toneChip(status: string): React.CSSProperties {
  if (status === "success") {
    return {
      background: "rgba(16,185,129,.14)",
      color: "#047857",
      border: "1px solid rgba(16,185,129,.22)",
    };
  }
  if (status === "error") {
    return {
      background: "rgba(239,68,68,.12)",
      color: "#b91c1c",
      border: "1px solid rgba(239,68,68,.18)",
    };
  }
  if (status === "skipped") {
    return {
      background: "rgba(245,158,11,.14)",
      color: "#92400e",
      border: "1px solid rgba(245,158,11,.18)",
    };
  }
  return {
    background: "rgba(59,130,246,.10)",
    color: "#1d4ed8",
    border: "1px solid rgba(59,130,246,.16)",
  };
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div style={smallCardStyle}>
      <div
        style={{
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 1,
          color: "#64748b",
          marginBottom: 12,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 30, fontWeight: 900, color: "#0f172a" }}>{value}</div>
      <div style={{ marginTop: 8, fontSize: 13, color: "#64748b" }}>{hint}</div>
    </div>
  );
}

function TopRowsTable({
  title,
  rows,
  primaryKey,
}: {
  title: string;
  rows: SeoDashboardRow[];
  primaryKey: "page" | "query" | "device";
}) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
        {title}
      </div>
      {rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  {primaryKey === "page"
                    ? "Page"
                    : primaryKey === "query"
                      ? "Query"
                      : "Device"}
                </th>
                <th style={{ ...tableCellStyle, textAlign: "right", color: "#64748b" }}>
                  Clicks
                </th>
                <th style={{ ...tableCellStyle, textAlign: "right", color: "#64748b" }}>
                  Impressions
                </th>
                <th style={{ ...tableCellStyle, textAlign: "right", color: "#64748b" }}>
                  CTR
                </th>
                <th style={{ ...tableCellStyle, textAlign: "right", color: "#64748b" }}>
                  Avg Pos
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row[primaryKey] || "row"}-${index}`}>
                  <td style={{ ...tableCellStyle, maxWidth: 320 }}>
                    <div style={{ fontWeight: 700, wordBreak: "break-word" }}>
                      {row[primaryKey] || "Unknown"}
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    {formatInteger(row.clicks)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    {formatInteger(row.impressions)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    {formatPercent(row.ctr)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    {formatPosition(row.position)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: "#64748b", fontSize: 14 }}>
          No Search Console data available yet for this view.
        </div>
      )}
    </div>
  );
}

function SitemapTable({ rows }: { rows: SeoDashboardSitemap[] }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
        Search Console Sitemaps
      </div>
      {rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Path
                </th>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Type
                </th>
                <th style={{ ...tableCellStyle, textAlign: "right", color: "#64748b" }}>
                  Errors
                </th>
                <th style={{ ...tableCellStyle, textAlign: "right", color: "#64748b" }}>
                  Warnings
                </th>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Last Submitted
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.path}>
                  <td style={{ ...tableCellStyle, wordBreak: "break-word" }}>{row.path}</td>
                  <td style={tableCellStyle}>{row.type || "Unknown"}</td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    {formatInteger(row.errors)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>
                    {formatInteger(row.warnings)}
                  </td>
                  <td style={tableCellStyle}>{formatDateTime(row.lastSubmitted)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: "#64748b", fontSize: 14 }}>
          No sitemaps returned from Search Console yet.
        </div>
      )}
    </div>
  );
}

function ActivityTable({ rows }: { rows: SeoActivity[] }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
        Recent SEO Activity
      </div>
      {rows.length ? (
        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 420 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    ...tableCellStyle,
                    textAlign: "left",
                    color: "#64748b",
                    position: "sticky",
                    top: 0,
                    background: "#f8fafc",
                    zIndex: 1,
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    ...tableCellStyle,
                    textAlign: "left",
                    color: "#64748b",
                    position: "sticky",
                    top: 0,
                    background: "#f8fafc",
                    zIndex: 1,
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    ...tableCellStyle,
                    textAlign: "left",
                    color: "#64748b",
                    position: "sticky",
                    top: 0,
                    background: "#f8fafc",
                    zIndex: 1,
                  }}
                >
                  Summary
                </th>
                <th
                  style={{
                    ...tableCellStyle,
                    textAlign: "left",
                    color: "#64748b",
                    position: "sticky",
                    top: 0,
                    background: "#f8fafc",
                    zIndex: 1,
                  }}
                >
                  Source
                </th>
                <th
                  style={{
                    ...tableCellStyle,
                    textAlign: "left",
                    color: "#64748b",
                    position: "sticky",
                    top: 0,
                    background: "#f8fafc",
                    zIndex: 1,
                  }}
                >
                  Time
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._id || `${row.type}-${row.createdAt}`}>
                  <td style={tableCellStyle}>
                    <span
                      style={{
                        ...toneChip(row.status),
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 999,
                        padding: "4px 10px",
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td style={tableCellStyle}>{row.type}</td>
                  <td style={{ ...tableCellStyle, minWidth: 260 }}>
                    <div style={{ fontWeight: 700 }}>{row.summary || "No summary"}</div>
                    {row.targetUrl ? (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          color: "#64748b",
                          wordBreak: "break-word",
                        }}
                      >
                        {row.targetUrl}
                      </div>
                    ) : null}
                    {row.errorMessage ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c" }}>
                        {row.errorMessage}
                      </div>
                    ) : null}
                  </td>
                  <td style={tableCellStyle}>{row.source}</td>
                  <td style={tableCellStyle}>{formatDateTime(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: "#64748b", fontSize: 14 }}>
          No SEO activity has been logged yet.
        </div>
      )}
    </div>
  );
}

function deriveUrlStatusLabel(row: SeoUrlStatus) {
  if (row.isIndexed) return "Indexed";
  if (row.lastInspectionStatus === "error") return "Inspection Error";
  if (row.lastInspectionStatus === "never") return "Uninspected";
  if (row.lastInspectionStatus === "skipped") return "Skipped";
  return "Not Indexed";
}

function deriveUrlStatusTone(row: SeoUrlStatus): React.CSSProperties {
  if (row.isIndexed) return toneChip("success");
  if (row.lastInspectionStatus === "error") return toneChip("error");
  if (row.lastInspectionStatus === "skipped") return toneChip("skipped");
  return {
    background: "rgba(245,158,11,.14)",
    color: "#92400e",
    border: "1px solid rgba(245,158,11,.18)",
  };
}

function UrlStatusTable({
  rows,
  counts,
  filter,
  onFilterChange,
  selectedUrl,
  onSelect,
  onInspect,
  onIndex,
  busyAction,
}: {
  rows: SeoUrlStatus[];
  counts: ReturnType<typeof buildUrlStatusCounts>;
  filter: UrlStatusFilter;
  onFilterChange: (filter: UrlStatusFilter) => void;
  selectedUrl: string;
  onSelect: (row: SeoUrlStatus) => void;
  onInspect: (url: string) => void;
  onIndex: (url: string) => void;
  busyAction: string;
}) {
  const filterOptions: Array<{
    key: UrlStatusFilter;
    label: string;
    count: number;
  }> = [
    { key: "all", label: "All", count: counts.all },
    { key: "indexed", label: "Indexed", count: counts.indexed },
    { key: "not_indexed", label: "Not Indexed", count: counts.not_indexed },
    { key: "errors", label: "Errors", count: counts.errors },
    { key: "uninspected", label: "Uninspected", count: counts.uninspected },
  ];

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
        All Site Links
      </div>
      <div style={{ ...subtleTextStyle, marginBottom: 14 }}>
        Track every sitemap URL, then switch between indexed, not-indexed, error, and
        uninspected views.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {filterOptions.map((option) => {
          const active = filter === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onFilterChange(option.key)}
              style={{
                ...buttonBase,
                padding: "8px 12px",
                fontSize: 12,
                background: active ? "#0f172a" : "rgba(15,23,42,.05)",
                color: active ? "#fff" : "#334155",
              }}
            >
              {option.label} ({formatInteger(option.count)})
            </button>
          );
        })}
      </div>
      {rows.length ? (
        <div style={{ overflowX: "auto", maxHeight: 520, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Status
                </th>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  URL
                </th>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Source
                </th>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Last Checked
                </th>
                <th style={{ ...tableCellStyle, textAlign: "left", color: "#64748b" }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const selected = selectedUrl === row.url;
                return (
                  <tr
                    key={row.url}
                    style={{
                      background: selected ? "rgba(14,165,233,.08)" : "transparent",
                      cursor: "pointer",
                    }}
                    onClick={() => onSelect(row)}
                  >
                    <td style={tableCellStyle}>
                      <span
                        style={{
                          ...deriveUrlStatusTone(row),
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: 999,
                          padding: "4px 10px",
                          fontWeight: 800,
                          fontSize: 12,
                        }}
                      >
                        {deriveUrlStatusLabel(row)}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, minWidth: 260, wordBreak: "break-all" }}>
                      {row.url}
                    </td>
                    <td style={tableCellStyle}>
                      {row.sourceType}
                      {row.sourceSlug ? `: ${row.sourceSlug}` : ""}
                    </td>
                    <td style={tableCellStyle}>
                      {formatDateTime(row.lastInspectedAt || row.updatedAt)}
                    </td>
                    <td style={tableCellStyle}>
                      {row.isIndexed ? (
                        <span
                          style={{
                            ...toneChip("success"),
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: 999,
                            padding: "7px 12px",
                            fontWeight: 800,
                            fontSize: 12,
                          }}
                        >
                          Success
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onIndex(row.url);
                          }}
                          disabled={busyAction !== ""}
                          title="Runs the best available indexing actions for this URL: sitemap submit plus a fresh inspection result."
                          style={{
                            ...buttonBase,
                            background: "rgba(245,158,11,.16)",
                            color: "#92400e",
                            padding: "8px 12px",
                            fontSize: 12,
                          }}
                        >
                          {busyAction === "index-url" ? "Indexing..." : "Index"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onInspect(row.url);
                        }}
                        title="Run Google URL inspection for this URL."
                        style={{
                          ...buttonBase,
                          background: "rgba(15,23,42,.08)",
                          color: "#0f172a",
                          padding: "8px 12px",
                          fontSize: 12,
                          marginLeft: 8,
                        }}
                      >
                        Inspect
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: "#64748b", fontSize: 14 }}>
          URL statuses will appear here after the sitemap is generated.
        </div>
      )}
    </div>
  );
}

export default function SeoDashboardPage() {
  const isCompact = useViewportMatch("(max-width: 960px)");
  const [dashboard, setDashboard] = useState<SeoDashboard | null>(null);
  const [form, setForm] = useState<SeoFormState>(defaultFormState);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [inspectionUrl, setInspectionUrl] = useState("");
  const [inspectionResponse, setInspectionResponse] =
    useState<SeoActionResponse | null>(null);
  const [sitemapXml, setSitemapXml] = useState("");
  const [selectedUrlStatus, setSelectedUrlStatus] = useState<SeoUrlStatus | null>(null);
  const [urlStatusFilter, setUrlStatusFilter] = useState<UrlStatusFilter>("all");
  const [inspectLoopProgress, setInspectLoopProgress] =
    useState<InspectLoopProgress | null>(null);

  const urlStatusCounts = useMemo(
    () => buildUrlStatusCounts(dashboard?.urlStatuses || []),
    [dashboard?.urlStatuses],
  );
  const visibleUrlStatuses = useMemo(
    () => filterUrlStatuses(dashboard?.urlStatuses || [], urlStatusFilter),
    [dashboard?.urlStatuses, urlStatusFilter],
  );

  async function loadDashboard(preserveForm = false) {
    setLoading(true);
    setError("");
    try {
      const data = await SeoAPI.dashboard();
      setDashboard(data);
      if (!preserveForm) {
        setForm(settingsToForm(data.settings));
        setInspectionUrl((current) =>
          current ||
          data.sitemap.previewUrls[0] ||
          joinSiteUrl(data.settings.siteUrl, "/customize"),
        );
      }
      setSelectedUrlStatus((current) => {
        if (current) {
          const next = data.urlStatuses.find((row) => row.url === current.url);
          if (next) return next;
        }
        return (
          data.urlStatuses.find((row) => !row.isIndexed) ||
          data.urlStatuses[0] ||
          null
        );
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load SEO dashboard.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadDashboard();
  }, []);

  useEffect(() => {
    if (!visibleUrlStatuses.length) return;
    if (
      selectedUrlStatus &&
      visibleUrlStatuses.some((row) => row.url === selectedUrlStatus.url)
    ) {
      return;
    }
    setSelectedUrlStatus(visibleUrlStatuses[0]);
    setInspectionUrl(visibleUrlStatuses[0].url);
  }, [selectedUrlStatus, visibleUrlStatuses]);

  async function runAction(
    key: string,
    runner: () => Promise<SeoActionResponse>,
    successMessage: string,
    options?: { reload?: boolean; preserveForm?: boolean },
  ) {
    setBusyAction(key);
    setNotice("");
    setError("");
    try {
      const result = await runner();
      setNotice(String(result.message || successMessage));
      return result;
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Action failed.");
      return null;
    } finally {
      setBusyAction("");
      if (options?.reload !== false) {
        void loadDashboard(options?.preserveForm ?? true);
      }
    }
  }

  async function handleSaveSettings() {
    setBusyAction("save");
    setNotice("");
    setError("");
    try {
      const savedSettings = await SeoAPI.updateSettings({
        siteUrl: form.siteUrl,
        searchConsolePropertyUrl: form.searchConsolePropertyUrl,
        sitemapPath: form.sitemapPath,
        serviceAccountJson: form.serviceAccountJson.trim() || undefined,
        autoSubmitSitemapOnProductChange: form.autoSubmitSitemapOnProductChange,
        autoInspectUpdatedUrls: form.autoInspectUpdatedUrls,
        autoSubmitSitemapOnContentChange: form.autoSubmitSitemapOnContentChange,
        autoSyncLocalSitemapSnapshot: form.autoSyncLocalSitemapSnapshot,
        fullSyncInspectLimit: form.fullSyncInspectLimit,
      });
      setForm((current) => ({
        ...current,
        siteUrl: savedSettings.siteUrl || "https://beyondpendants.com/",
        searchConsolePropertyUrl:
          savedSettings.searchConsolePropertyUrl || "sc-domain:beyondpendants.com",
        sitemapPath: savedSettings.sitemapPath || "/sitemap.xml",
        fullSyncInspectLimit: Number(savedSettings.fullSyncInspectLimit || 0),
      }));

      if (savedSettings.hasServiceAccountCredentials) {
        try {
          const connection = await SeoAPI.testConnection();
          setNotice(
            connection.ok
              ? "SEO settings saved. Service account JSON is valid and Search Console access is working."
              : String(connection.message || "SEO settings saved."),
          );
        } catch (connectionError: any) {
          setNotice("SEO settings saved.");
          setError(
            `Service account JSON was saved, but Search Console access is still failing. ${
              connectionError?.response?.data?.message ||
              connectionError?.message ||
              "Connection test failed."
            }`,
          );
        }
      } else {
        setNotice("SEO settings saved.");
      }
      void loadDashboard(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to save SEO settings.",
      );
    } finally {
      setBusyAction("");
    }
  }

  async function handleClearStoredCredentials() {
    if (!window.confirm("Remove the stored Search Console service-account credentials?")) {
      return;
    }
    setBusyAction("clear-credentials");
    setNotice("");
    setError("");
    try {
      await SeoAPI.updateSettings({
        siteUrl: form.siteUrl,
        searchConsolePropertyUrl: form.searchConsolePropertyUrl,
        sitemapPath: form.sitemapPath,
        autoSubmitSitemapOnProductChange: form.autoSubmitSitemapOnProductChange,
        autoInspectUpdatedUrls: form.autoInspectUpdatedUrls,
        autoSubmitSitemapOnContentChange: form.autoSubmitSitemapOnContentChange,
        autoSyncLocalSitemapSnapshot: form.autoSyncLocalSitemapSnapshot,
        fullSyncInspectLimit: form.fullSyncInspectLimit,
        clearServiceAccount: true,
      });
      setForm((current) => ({ ...current, serviceAccountJson: "" }));
      setNotice("Stored service-account credentials cleared.");
      void loadDashboard();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to clear stored Search Console credentials.",
      );
    } finally {
      setBusyAction("");
    }
  }

  async function inspectSpecificUrl(url: string) {
    const result = await runAction(
      "inspect",
      () => SeoAPI.inspectUrl(url),
      "URL inspection finished.",
      { reload: true, preserveForm: true },
    );
    if (result) setInspectionResponse(result);
  }

  async function handleInspectUrl() {
    await inspectSpecificUrl(inspectionUrl);
  }

  async function handleIndexAllUrls() {
    const inspectionUrls = (dashboard?.urlStatuses || [])
      .map((row) => row.url)
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 5000);
    if (!inspectionUrls.length) {
      setError("No tracked URLs are available to inspect yet.");
      return;
    }

    const startedAt = new Date().toISOString();
    const total = inspectionUrls.length;
    let successCount = 0;
    let indexedCount = 0;
    let notIndexedCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    setBusyAction("index-all");
    setNotice("");
    setError("");
    setInspectLoopProgress({
      active: true,
      total,
      completed: 0,
      successCount: 0,
      indexedCount: 0,
      notIndexedCount: 0,
      errorCount: 0,
      skippedCount: 0,
      currentUrl: "",
      lastMessage: "Submitting sitemap before the indexing-status run...",
      startedAt,
    });

    try {
      await SeoAPI.submitSitemap();
      setInspectLoopProgress({
        active: true,
        total,
        completed: 0,
        successCount,
        indexedCount,
        notIndexedCount,
        errorCount,
        skippedCount,
        currentUrl: "",
        lastMessage: "Sitemap submitted. Checking indexing status across tracked URLs...",
        startedAt,
      });

      for (let index = 0; index < total; index += 1) {
        const url = inspectionUrls[index];
        setInspectionUrl(url);
        setInspectLoopProgress({
          active: true,
          total,
          completed: index,
          successCount,
          indexedCount,
          notIndexedCount,
          errorCount,
          skippedCount,
          currentUrl: url,
          lastMessage: `Checking ${formatInteger(index + 1)} of ${formatInteger(total)}.`,
          startedAt,
        });

        try {
          const result = await SeoAPI.inspectUrl(url);
          setInspectionResponse(result);
          if (result.ok) {
            successCount += 1;
            if (inspectionResponseIsIndexed(result)) indexedCount += 1;
            else notIndexedCount += 1;
          } else if (result.skipped) {
            skippedCount += 1;
          } else {
            errorCount += 1;
          }

          setInspectLoopProgress({
            active: true,
            total,
            completed: index + 1,
            successCount,
            indexedCount,
            notIndexedCount,
            errorCount,
            skippedCount,
            currentUrl: url,
            lastMessage: String(
              result.message ||
                (result.ok
                  ? `Indexing status refreshed for ${url}.`
                  : `Indexing status check failed for ${url}.`),
            ),
            startedAt,
          });
        } catch (err: any) {
          errorCount += 1;
          const message =
            err?.response?.data?.message ||
            err?.message ||
            `Indexing status check failed for ${url}.`;
          setInspectLoopProgress({
            active: true,
            total,
            completed: index + 1,
            successCount,
            indexedCount,
            notIndexedCount,
            errorCount,
            skippedCount,
            currentUrl: url,
            lastMessage: message,
            startedAt,
          });
        }
      }

      setNotice(
        `Indexing-status run finished. ${formatInteger(indexedCount)} indexed, ${formatInteger(
          notIndexedCount,
        )} not indexed, ${formatInteger(errorCount)} failed, and ${formatInteger(
          skippedCount,
        )} were skipped across ${formatInteger(inspectionUrls.length)} URLs.`,
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to run indexing actions for all tracked URLs.",
      );
    } finally {
      setBusyAction("");
      setInspectLoopProgress({
        active: false,
        total,
        completed: total,
        successCount,
        indexedCount,
        notIndexedCount,
        errorCount,
        skippedCount,
        currentUrl: inspectionUrls[total - 1] || "",
        lastMessage: "Bulk indexing-status run finished.",
        startedAt,
        finishedAt: new Date().toISOString(),
      });
      void loadDashboard(true);
    }
  }

  async function handleIndexUrl(url: string) {
    setBusyAction("index-url");
    setNotice("");
    setError("");
    try {
      await SeoAPI.submitSitemap();
      const result = await SeoAPI.inspectUrl(url);
      setInspectionResponse(result);
      setInspectionUrl(url);
      setNotice(
        "Submitted the sitemap and refreshed the indexing status for this URL. Google does not provide a force-index API for normal ecommerce pages, so this is the strongest supported action.",
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to run indexing actions for this URL.",
      );
    } finally {
      setBusyAction("");
      void loadDashboard(true);
    }
  }

  async function copyAllUrls() {
    const text = String(dashboard?.sitemap.copyableUrlList || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setNotice("Copied all sitemap URLs.");
    } catch {
      setError("Failed to copy URLs to clipboard.");
    }
  }

  async function handleLoadSitemapXml() {
    setBusyAction("load-sitemap-xml");
    setError("");
    try {
      const xml = await SeoAPI.sitemapXml();
      setSitemapXml(xml);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load sitemap XML.");
    } finally {
      setBusyAction("");
    }
  }

  if (loading && !dashboard) {
    return (
      <div style={{ padding: 24, color: "#0f172a", fontWeight: 700 }}>
        Loading SEO dashboard...
      </div>
    );
  }

  const settings = dashboard?.settings;
  const performance = dashboard?.searchConsole.performance;
  const selectedDetails =
    selectedUrlStatus &&
    dashboard?.urlStatuses.find((row) => row.url === selectedUrlStatus.url);
  const jsonFeedback = getServiceAccountJsonFeedback(
    form.serviceAccountJson,
    Boolean(settings?.hasServiceAccountCredentials),
  );
  const connectionSuggestions = getConnectionSuggestions(
    dashboard?.searchConsole.connection,
    form.searchConsolePropertyUrl || settings?.searchConsolePropertyUrl || "",
    settings?.serviceAccountClientEmail || "",
  );
  const effectiveConnectionStatus =
    dashboard?.searchConsole.configured
      ? dashboard.searchConsole.connection.ok
        ? "success"
        : "error"
      : settings?.lastConnectionStatus || "unknown";
  const effectiveConnectionMessage =
    dashboard?.searchConsole.connection.message ||
    settings?.lastConnectionMessage ||
    "No connection test has been run yet.";
  const effectiveLastCheck =
    settings?.lastConnectionCheckAt ||
    (dashboard?.searchConsole.configured ? "Live dashboard validation" : "");
  const inspectLoopPercent = inspectLoopProgress?.total
    ? Math.max(
        0,
        Math.min(
          100,
          Math.round((inspectLoopProgress.completed / inspectLoopProgress.total) * 100),
        ),
      )
    : 0;

  return (
    <div
      style={{
        padding: "clamp(14px, 3vw, 24px)",
        display: "grid",
        gap: isCompact ? 16 : 20,
        overflowX: "hidden",
        background:
          "radial-gradient(circle at top right, rgba(14,165,233,.14), transparent 26%), linear-gradient(180deg, #f8fafc 0%, #eef6ff 100%)",
      }}
    >
      <div style={{ ...cardStyle, padding: isCompact ? 18 : 24, display: "grid", gap: 14 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <div
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1.6,
                color: "#0f766e",
                fontWeight: 800,
              }}
            >
              Search Console + Sitemap Automation
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(28px, 5vw, 34px)",
                lineHeight: 1,
                color: "#0f172a",
              }}
            >
              SEO Dashboard
            </h1>
            <div style={{ color: "#475569", fontSize: 15, maxWidth: 780 }}>
              Manage Search Console credentials, submit or inspect URLs, preview your sitemap,
              and track automated SEO actions triggered when products or content change.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() =>
                void runAction(
                  "test-connection",
                  () => SeoAPI.testConnection(),
                  "Connection test completed.",
                )
              }
              disabled={busyAction !== ""}
              title="Checks whether the saved Google service account can access the configured Search Console property."
              style={{
                ...buttonBase,
                background: "#0f172a",
                color: "#fff",
                ...(isCompact ? { flex: "1 1 100%", justifyContent: "center" } : {}),
              }}
            >
              {busyAction === "test-connection" ? "Testing..." : "Test Connection"}
            </button>
            <button
              type="button"
              onClick={() =>
                void runAction(
                  "submit-sitemap",
                  () => SeoAPI.submitSitemap(),
                  "Sitemap submitted.",
                )
              }
              disabled={busyAction !== ""}
              title="Submits the current sitemap URL to Google Search Console."
              style={{
                ...buttonBase,
                background: "#0ea5e9",
                color: "#fff",
                ...(isCompact ? { flex: "1 1 100%", justifyContent: "center" } : {}),
              }}
            >
              {busyAction === "submit-sitemap" ? "Submitting..." : "Submit Sitemap"}
            </button>
            <button
              type="button"
              onClick={() => void handleIndexAllUrls()}
              disabled={busyAction !== ""}
              title="Runs the supported indexing actions for every tracked URL: sitemap submission plus a fresh URL inspection, then stores indexed vs not-indexed results."
              style={{
                ...buttonBase,
                background: "#f59e0b",
                color: "#111827",
                ...(isCompact ? { flex: "1 1 100%", justifyContent: "center" } : {}),
              }}
            >
              {busyAction === "index-all"
                ? `Checking ${formatInteger(inspectLoopProgress?.completed || 0)}/${formatInteger(
                    inspectLoopProgress?.total || 0,
                  )}...`
                : "Run Indexing Actions"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span
            style={{
              ...toneChip(effectiveConnectionStatus),
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              padding: "7px 12px",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Connection: {effectiveConnectionStatus}
          </span>
          <span
            style={{
              background: "rgba(14,165,233,.10)",
              color: "#0369a1",
              border: "1px solid rgba(14,165,233,.18)",
              borderRadius: 999,
              padding: "7px 12px",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Sitemap URLs: {formatInteger(dashboard?.sitemap.entryCount || 0)}
          </span>
          <span
            style={{
              background: "rgba(15,23,42,.06)",
              color: "#334155",
              border: "1px solid rgba(148,163,184,.18)",
              borderRadius: 999,
              padding: "7px 12px",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Property: {settings?.searchConsolePropertyUrl || "Not configured"}
          </span>
          <span
            style={{
              background: "rgba(16,185,129,.10)",
              color: "#047857",
              border: "1px solid rgba(16,185,129,.18)",
              borderRadius: 999,
              padding: "7px 12px",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Available Links: {formatInteger(dashboard?.urlStatuses.length || 0)}
          </span>
        </div>

        {notice ? (
          <div
            style={{
              borderRadius: 16,
              padding: "12px 14px",
              background: "rgba(16,185,129,.10)",
              border: "1px solid rgba(16,185,129,.18)",
              color: "#047857",
              fontWeight: 700,
            }}
          >
            {notice}
          </div>
        ) : null}
        {error ? (
          <div
            style={{
              borderRadius: 16,
              padding: "12px 14px",
              background: "rgba(239,68,68,.08)",
              border: "1px solid rgba(239,68,68,.16)",
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        ) : null}
        {inspectLoopProgress ? (
          <div
            style={{
              borderRadius: 18,
              padding: "14px 16px",
              background: "rgba(15,23,42,.04)",
              border: "1px solid rgba(148,163,184,.18)",
              display: "grid",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: 900, color: "#0f172a" }}>
                Bulk Indexing Status Run
              </div>
              <div style={{ color: "#475569", fontSize: 13, fontWeight: 700 }}>
                {inspectLoopProgress.active ? "Running" : "Completed"} •{" "}
                {formatInteger(inspectLoopProgress.completed)} /{" "}
                {formatInteger(inspectLoopProgress.total)} • {inspectLoopPercent}%
              </div>
            </div>
            <div
              style={{
                height: 10,
                borderRadius: 999,
                overflow: "hidden",
                background: "rgba(148,163,184,.22)",
              }}
            >
              <div
                style={{
                  width: `${inspectLoopPercent}%`,
                  height: "100%",
                  background:
                    "linear-gradient(90deg, rgba(14,165,233,1) 0%, rgba(16,185,129,1) 100%)",
                  transition: "width .2s ease",
                }}
              />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, color: "#334155" }}>
              <div>
                <strong>Success:</strong> {formatInteger(inspectLoopProgress.successCount)}
              </div>
              <div>
                <strong>Indexed:</strong> {formatInteger(inspectLoopProgress.indexedCount)}
              </div>
              <div>
                <strong>Not Indexed:</strong> {formatInteger(inspectLoopProgress.notIndexedCount)}
              </div>
              <div>
                <strong>Errors:</strong> {formatInteger(inspectLoopProgress.errorCount)}
              </div>
              <div>
                <strong>Skipped:</strong> {formatInteger(inspectLoopProgress.skippedCount)}
              </div>
              <div>
                <strong>Started:</strong> {formatDateTime(inspectLoopProgress.startedAt)}
              </div>
              {inspectLoopProgress.finishedAt ? (
                <div>
                  <strong>Finished:</strong> {formatDateTime(inspectLoopProgress.finishedAt)}
                </div>
              ) : null}
            </div>
            <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
              <strong>Current URL:</strong>{" "}
              <span style={{ wordBreak: "break-all" }}>
                {inspectLoopProgress.currentUrl || "Waiting to start..."}
              </span>
            </div>
            <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
              {inspectLoopProgress.lastMessage}
            </div>
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gap: 20,
          gridTemplateColumns: responsiveGrid(320),
        }}
      >
        <div style={{ ...cardStyle, display: "grid", gap: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>SEO Settings</div>
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: responsiveGrid(260),
            }}
          >
            <label style={labelStyle}>
              Website URL
              <input
                style={inputStyle}
                value={form.siteUrl}
                onChange={(event) =>
                  setForm((current) => ({ ...current, siteUrl: event.target.value }))
                }
                placeholder="https://beyondpendants.com/"
              />
            </label>
            <label style={labelStyle}>
              Search Console Property
              <input
                style={inputStyle}
                value={form.searchConsolePropertyUrl}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    searchConsolePropertyUrl: event.target.value,
                  }))
                }
                placeholder="sc-domain:beyondpendants.com"
              />
            </label>
            <label style={labelStyle}>
              Sitemap Path
              <input
                style={inputStyle}
                value={form.sitemapPath}
                onChange={(event) =>
                  setForm((current) => ({ ...current, sitemapPath: event.target.value }))
                }
                placeholder="/sitemap.xml"
              />
            </label>
            <label style={labelStyle}>
              Full Sync Inspect Limit
              <input
                style={inputStyle}
                type="number"
                min={0}
                max={5000}
                value={form.fullSyncInspectLimit}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    fullSyncInspectLimit: Number(event.target.value || 0),
                  }))
                }
              />
            </label>
          </div>

          <label style={labelStyle}>
            Service Account JSON
            <textarea
              style={textareaStyle}
              value={form.serviceAccountJson}
              onChange={(event) =>
                setForm((current) => ({ ...current, serviceAccountJson: event.target.value }))
              }
              placeholder='Paste the Google Cloud service-account JSON here. The backend stores it encrypted and never returns the private key back to the browser.'
            />
          </label>
          <div
            style={{
              ...feedbackStyle(jsonFeedback.tone),
              borderRadius: 16,
              padding: "12px 14px",
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontWeight: 800 }}>{jsonFeedback.title}</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>{jsonFeedback.detail}</div>
            {(jsonFeedback.items || []).length ? (
              <div style={{ display: "grid", gap: 4 }}>
                {(jsonFeedback.items || []).map((item) => (
                  <div key={item} style={{ fontSize: 13, lineHeight: 1.45 }}>
                    {item}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div style={subtleTextStyle}>
            Your pasted example is not a working Google key if `private_key` still says placeholder
            text like `key is here`. The admin panel now flags that before save, and the backend
            will also reject it.
          </div>

          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: responsiveGrid(260),
            }}
          >
            {[
              {
                key: "autoSubmitSitemapOnProductChange",
                label: "Auto-submit sitemap when products change",
              },
              {
                key: "autoInspectUpdatedUrls",
                label: "Auto-inspect changed product URLs",
              },
              {
                key: "autoSubmitSitemapOnContentChange",
                label: "Auto-submit sitemap when site content changes",
              },
              {
                key: "autoSyncLocalSitemapSnapshot",
                label: "Auto-sync local sitemap files in this repo",
              },
            ].map((toggle) => (
              <label
                key={toggle.key}
                style={{
                  ...smallCardStyle,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(form[toggle.key as keyof SeoFormState])}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      [toggle.key]: event.target.checked,
                    }))
                  }
                />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>{toggle.label}</span>
              </label>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ color: "#64748b", fontSize: 13 }}>
              Stored service account:{" "}
              <strong style={{ color: "#0f172a" }}>
                {settings?.hasServiceAccountCredentials
                  ? settings.serviceAccountClientEmail || "Stored"
                  : "Not stored"}
              </strong>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => void handleClearStoredCredentials()}
                disabled={busyAction !== ""}
                title="Deletes the encrypted service-account key currently stored on the backend."
                style={{
                  ...buttonBase,
                  background: "rgba(239,68,68,.10)",
                  color: "#b91c1c",
                }}
              >
                {busyAction === "clear-credentials" ? "Clearing..." : "Clear Stored Key"}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveSettings()}
                disabled={busyAction !== ""}
                title="Saves the SEO settings and encrypted Search Console credentials, then tests access."
                style={{ ...buttonBase, background: "#111827", color: "#fff" }}
              >
                {busyAction === "save" ? "Saving..." : "Save SEO Settings"}
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 20, alignContent: "start", minWidth: 0 }}>
          <div style={cardStyle}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
              Connection Health
            </div>
            <div style={{ display: "grid", gap: 10, color: "#334155", fontSize: 14 }}>
              <div>
                <strong>Status:</strong> {effectiveConnectionStatus}
              </div>
              <div>
                <strong>State:</strong>{" "}
                {dashboard?.searchConsole.connection.state || "unknown"}
              </div>
              <div>
                <strong>Last Check:</strong>{" "}
                {effectiveLastCheck === "Live dashboard validation"
                  ? effectiveLastCheck
                  : formatDateTime(effectiveLastCheck)}
              </div>
              <div>
                <strong>Message:</strong>{" "}
                {effectiveConnectionMessage}
              </div>
              <div>
                <strong>Search Console:</strong>{" "}
                {dashboard?.searchConsole.connection.ok ? "Connected" : "Not connected"}
              </div>
              <div>
                <strong>Effective Site URL:</strong>{" "}
                {dashboard?.searchConsole.connection.siteUrl || settings?.siteUrl || "Not configured"}
              </div>
              <div>
                <strong>Exact Property Checked:</strong>{" "}
                {dashboard?.searchConsole.connection.exactPropertyChecked ||
                  settings?.searchConsolePropertyUrl ||
                  "Not configured"}
              </div>
              <div>
                <strong>Credentials Loaded:</strong>{" "}
                {dashboard?.searchConsole.connection.hasCredentials ? "Yes" : "No"}
              </div>
              <div>
                <strong>Credential Email:</strong>{" "}
                {dashboard?.searchConsole.connection.credentialEmail ||
                  settings?.serviceAccountClientEmail ||
                  "None"}
              </div>
              <div>
                <strong>Expected Email:</strong>{" "}
                {dashboard?.searchConsole.connection.expectedCredentialEmail || "Not set"}
              </div>
              <div>
                <strong>Credential Source:</strong>{" "}
                {dashboard?.searchConsole.connection.credentialSource || "unknown"}
              </div>
              {dashboard?.searchConsole.connection.googleErrorMessage ? (
                <div>
                  <strong>Google Error:</strong>{" "}
                  {dashboard.searchConsole.connection.googleErrorMessage}
                </div>
              ) : null}
            </div>
            {!dashboard?.searchConsole.connection.ok ? (
              <div
                style={{
                  marginTop: 14,
                  borderRadius: 16,
                  padding: "12px 14px",
                  background: "rgba(245,158,11,.10)",
                  border: "1px solid rgba(245,158,11,.18)",
                  color: "#92400e",
                  display: "grid",
                  gap: 8,
                }}
              >
                <div style={{ fontWeight: 800 }}>Connection Fixes</div>
                {connectionSuggestions.map((item) => (
                  <div key={item} style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {item}
                  </div>
                ))}
              </div>
            ) : null}
            <div
              style={{
                marginTop: 14,
                borderRadius: 16,
                padding: "12px 14px",
                background: "rgba(15,23,42,.04)",
                border: "1px solid rgba(148,163,184,.18)",
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                Service Account Accessible Properties
              </div>
              {(dashboard?.searchConsole.connection.accessibleSites || []).length ? (
                (dashboard?.searchConsole.connection.accessibleSites || []).map((item) => (
                  <div
                    key={`${item.siteUrl}-${item.permissionLevel || ""}`}
                    style={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: "#334155",
                      wordBreak: "break-all",
                    }}
                  >
                    {item.siteUrl}
                    {item.permissionLevel ? ` (${item.permissionLevel})` : ""}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
                  Google did not return any accessible Search Console properties for this service
                  account yet.
                </div>
              )}
            </div>
          </div>

          <div style={cardStyle}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
              Sitemap Snapshot
            </div>
            <div style={{ display: "grid", gap: 10, color: "#334155", fontSize: 14 }}>
              <div>
                <strong>Live URL:</strong>{" "}
                <span style={{ wordBreak: "break-all" }}>
                  {dashboard?.sitemap.sitemapUrl || "Not available"}
                </span>
              </div>
              <div>
                <strong>Total URLs:</strong> {formatInteger(dashboard?.sitemap.entryCount || 0)}
              </div>
              <div>
                <strong>Products:</strong> {formatInteger(dashboard?.sitemap.productCount || 0)}
              </div>
              <div>
                <strong>Generated:</strong> {formatDateTime(dashboard?.sitemap.generatedAt)}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
              <button
                type="button"
                onClick={() =>
                  void runAction(
                    "submit-sitemap-now",
                    () => SeoAPI.submitSitemap(),
                    "Sitemap submitted now.",
                  )
                }
                disabled={busyAction !== ""}
                title="Immediately submits the current sitemap URL to Google Search Console."
                style={{
                  ...buttonBase,
                  background: "#0ea5e9",
                  color: "#fff",
                }}
              >
                {busyAction === "submit-sitemap-now" ? "Submitting..." : "Submit Sitemap Now"}
              </button>
              <button
                type="button"
                onClick={() =>
                  void runAction(
                    "sync-local-sitemap",
                    () => SeoAPI.syncLocalSitemap(),
                    "Local sitemap sync finished.",
                  )
                }
                disabled={busyAction !== ""}
                title="Updates the local sitemap snapshot files in this repository to match the generated sitemap."
                style={{
                  ...buttonBase,
                  background: "rgba(15,23,42,.08)",
                  color: "#0f172a",
                }}
              >
                {busyAction === "sync-local-sitemap" ? "Syncing..." : "Sync Local Files"}
              </button>
              <button
                type="button"
                onClick={() => void handleLoadSitemapXml()}
                disabled={busyAction !== ""}
                title="Loads the generated sitemap XML so you can review exactly what will be submitted."
                style={{
                  ...buttonBase,
                  background: "rgba(14,165,233,.10)",
                  color: "#0369a1",
                }}
              >
                {busyAction === "load-sitemap-xml" ? "Loading..." : "Load XML Preview"}
              </button>
              <button
                type="button"
                onClick={() => void copyAllUrls()}
                title="Copies every URL currently included in the sitemap to your clipboard."
                style={{
                  ...buttonBase,
                  background: "rgba(16,185,129,.12)",
                  color: "#047857",
                }}
              >
                Copy All URLs
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gap: 16,
          gridTemplateColumns: responsiveGrid(220),
        }}
      >
        <MetricCard
          label="Clicks"
          value={formatInteger(performance?.clicks || 0)}
          hint={
            performance
              ? `${performance.startDate} to ${performance.endDate}`
              : "Waiting for Search Console data"
          }
        />
        <MetricCard
          label="Impressions"
          value={formatInteger(performance?.impressions || 0)}
          hint="Search Console search impressions"
        />
        <MetricCard
          label="CTR"
          value={formatPercent(performance?.ctr || 0)}
          hint="Average click-through rate"
        />
        <MetricCard
          label="Avg Position"
          value={formatPosition(performance?.position || 0)}
          hint="Lower is better"
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: 20,
          gridTemplateColumns: responsiveGrid(220),
        }}
      >
        <MetricCard
          label="Tracked URLs"
          value={formatInteger(dashboard?.urlStatusSummary.total || 0)}
          hint="URLs currently tracked in the SEO list"
        />
        <MetricCard
          label="Indexed URLs"
          value={formatInteger(dashboard?.urlStatusSummary.indexed || 0)}
          hint="URLs Google inspection marked as indexed"
        />
        <MetricCard
          label="Not Indexed"
          value={formatInteger(dashboard?.urlStatusSummary.nonIndexed || 0)}
          hint="Tracked URLs Google says are not indexed yet"
        />
        <MetricCard
          label="Inspect Errors"
          value={formatInteger(dashboard?.urlStatusSummary.errors || 0)}
          hint="URLs where Google inspection returned an error"
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: 20,
          gridTemplateColumns: responsiveGrid(320),
        }}
      >
        <TopRowsTable
          title="Top Pages"
          rows={dashboard?.searchConsole.topPages || []}
          primaryKey="page"
        />
        <TopRowsTable
          title="Top Queries"
          rows={dashboard?.searchConsole.topQueries || []}
          primaryKey="query"
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: 20,
          gridTemplateColumns: responsiveGrid(320),
        }}
      >
        <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
          <UrlStatusTable
            rows={visibleUrlStatuses}
            counts={urlStatusCounts}
            filter={urlStatusFilter}
            onFilterChange={setUrlStatusFilter}
            selectedUrl={selectedDetails?.url || ""}
            onSelect={(row) => {
              setSelectedUrlStatus(row);
              setInspectionUrl(row.url);
            }}
            onInspect={(url) => {
              setInspectionUrl(url);
              void inspectSpecificUrl(url);
            }}
            onIndex={(url) => {
              void handleIndexUrl(url);
            }}
            busyAction={busyAction}
          />
          <SitemapTable rows={dashboard?.searchConsole.sitemaps || []} />
          <ActivityTable rows={dashboard?.recentActivity || []} />
        </div>

        <div style={{ display: "grid", gap: 20, alignContent: "start", minWidth: 0 }}>
          <div style={cardStyle}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
              URL Inspection
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              <input
                style={inputStyle}
                value={inspectionUrl}
                onChange={(event) => setInspectionUrl(event.target.value)}
                placeholder="https://beyondpendants.com/products/your-slug"
              />
              <button
                type="button"
                onClick={() => void handleInspectUrl()}
                disabled={busyAction !== ""}
                title="Runs Google URL inspection for the URL above using the configured Search Console property."
                style={{
                  ...buttonBase,
                  background: "#0f172a",
                  color: "#fff",
                  justifySelf: "start",
                }}
              >
                {busyAction === "inspect" ? "Inspecting..." : "Inspect URL"}
              </button>
              <button
                type="button"
                onClick={() => void handleIndexAllUrls()}
                disabled={busyAction !== ""}
                title="Submits the sitemap, inspects every tracked URL, and refreshes which pages are indexed vs not indexed."
                style={{
                  ...buttonBase,
                  background: "rgba(245,158,11,.16)",
                  color: "#92400e",
                  justifySelf: "start",
                }}
              >
                {busyAction === "index-all"
                  ? `Checking ${formatInteger(
                      inspectLoopProgress?.completed || 0,
                    )}/${formatInteger(inspectLoopProgress?.total || 0)}...`
                  : "Run Indexing Actions for All URLs"}
              </button>
              <div style={{ color: "#64748b", fontSize: 13 }}>
                Google does not provide a general “force index this product page now” API. This
                dashboard uses sitemap submission and URL inspection, which are the supported
                Search Console actions for normal ecommerce pages.
              </div>
              <div style={subtleTextStyle}>
                For your current production error on `https://beyondpendants.com/customize`,
                `PERMISSION_DENIED` means the service-account email must be granted access to the
                exact property in Search Console, or the property scope does not include that URL.
              </div>
            </div>
            {inspectionResponse ? (
              <pre
                style={{
                  marginTop: 16,
                  padding: 14,
                  borderRadius: 16,
                  background: "#0f172a",
                  color: "#e2e8f0",
                  overflowX: "auto",
                  fontSize: 12,
                  lineHeight: 1.55,
                }}
              >
                {JSON.stringify(inspectionResponse, null, 2)}
              </pre>
            ) : null}
          </div>

          <div style={cardStyle}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
              Copyable Sitemap URL List
            </div>
            <textarea
              style={{ ...textareaStyle, minHeight: 220 }}
              readOnly
              value={dashboard?.sitemap.copyableUrlList || ""}
            />
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 10 }}>
              Copy this list into Google Search Console manually if you want an extra manual check.
            </div>
          </div>

          <div style={cardStyle}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
              Selected URL Suggestions
            </div>
            {selectedDetails ? (
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ wordBreak: "break-all", color: "#0f172a", fontWeight: 800 }}>
                  {selectedDetails.url}
                </div>
                <div style={{ color: "#334155", fontSize: 14 }}>
                  <strong>Status:</strong> {deriveUrlStatusLabel(selectedDetails)}
                </div>
                {selectedDetails.coverageState ? (
                  <div style={{ color: "#334155", fontSize: 14 }}>
                    <strong>Coverage:</strong> {selectedDetails.coverageState}
                  </div>
                ) : null}
                {selectedDetails.indexingState ? (
                  <div style={{ color: "#334155", fontSize: 14 }}>
                    <strong>Indexing State:</strong> {selectedDetails.indexingState}
                  </div>
                ) : null}
                {selectedDetails.pageFetchState ? (
                  <div style={{ color: "#334155", fontSize: 14 }}>
                    <strong>Page Fetch:</strong> {selectedDetails.pageFetchState}
                  </div>
                ) : null}
                {selectedDetails.robotsTxtState ? (
                  <div style={{ color: "#334155", fontSize: 14 }}>
                    <strong>Robots:</strong> {selectedDetails.robotsTxtState}
                  </div>
                ) : null}
                {selectedDetails.inspectionErrorMessage ? (
                  <div style={{ color: "#b91c1c", fontSize: 14 }}>
                    <strong>Error:</strong> {selectedDetails.inspectionErrorMessage}
                  </div>
                ) : null}
                <div style={{ display: "grid", gap: 8 }}>
                  {(selectedDetails.suggestions || []).map((item, index) => (
                    <div
                      key={`${selectedDetails.url}-suggestion-${index}`}
                      style={{
                        borderRadius: 14,
                        border: "1px solid rgba(148,163,184,.18)",
                        padding: "10px 12px",
                        background: "#fff",
                        fontSize: 14,
                        color: "#334155",
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ color: "#64748b", fontSize: 14 }}>
                Select a URL from the list to view suggestions.
              </div>
            )}
          </div>

          {sitemapXml ? (
            <div style={cardStyle}>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 14 }}>
                Sitemap XML
              </div>
              <textarea style={{ ...textareaStyle, minHeight: 320 }} value={sitemapXml} readOnly />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
